import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import { queryKeys } from '@/lib/query-keys'

interface GroupAssignment {
  mode: 'new' | 'existing';
  groupId?: string;
  groupName?: string;
}

interface ImportPayload {
  fileContent: string
  fileType: string
  columnMap: { [key: string]: any }
  workspaceId: string
  parsedData?: any[] // Optional parsed data for CSV imports
  groupAssignment?: GroupAssignment
  contactCount?: number // Number of contacts being imported for progress estimation
}

async function importContacts(payload: ImportPayload) {
  const { data, error } = await supabase.functions.invoke('import-contacts', {
    body: payload,
  })

  if (error) {
    throw new Error(error.message)
  }
  return data
}

async function undoImport(importBatchId: string) {
  const { data, error } = await supabase.functions.invoke('undo-contact-import', {
    body: { import_batch_id: importBatchId },
  })

  if (error) {
    throw new Error(error.message)
  }
  return data
}

export function useImportMutations() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { mutate: importContactsMutation, isPending: isImporting } = useMutation({
    mutationFn: importContacts,
    onSuccess: (data) => {
      // Silently invalidate queries - the dialog shows immediate confirmation
      queryClient.invalidateQueries({
        queryKey: queryKeys.importHistory.list(),
      })
      // Also invalidate contacts to show group membership
      if (data?.groupAssignment?.success) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.contacts.all,
        })
      }
    },
    onError: (error) => {
      toast({
        title: 'Import Failed',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const { mutate: undoImportMutation, isPending: isUndoing } = useMutation({
    mutationFn: undoImport,
    onSuccess: () => {
      toast({
        title: 'Undo Successful',
        description: 'The import has been reverted.',
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.importHistory.list(),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.contacts.all, // Invalidate all contact queries
      })
    },
    onError: (error) => {
      toast({
        title: 'Undo Failed',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  return {
    importContacts: importContactsMutation,
    isImporting,
    undoImport: undoImportMutation,
    isUndoing,
  }
}
